import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { songAPI } from '../services/api';

const PageContainer = styled.div`
  padding: 1rem;
  min-height: 60vh;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid rgba(204, 0, 0, 0.2);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    gap: 0.1rem;
  }
`;

const Tab = styled.button`
  background: transparent;
  color: ${props => props.active ? '#cc0000' : '#cccccc'};
  border: none;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border-bottom: 3px solid ${props => props.active ? '#cc0000' : 'transparent'};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;
  
  &:hover {
    color: #cc0000;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
  }
`;

const TabContent = styled.div`
  padding: 1rem 0;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(204, 0, 0, 0.1);
  border: 1px solid rgba(204, 0, 0, 0.3);
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #cc0000;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #cccccc;
  margin-top: 0.25rem;
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(204, 0, 0, 0.2);
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const AddForm = styled.form`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`;

const FormGroup = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #cccccc;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #444;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 1rem;
  
  &:focus {
    border-color: #cc0000;
    outline: none;
  }
`;

const Button = styled.button`
  background: #cc0000;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #aa0000;
  }
`;

const SongGrid = styled.div`
  display: grid;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const SongCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(204, 0, 0, 0.2);
  border-radius: 6px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: #cc0000;
    transform: translateY(-2px);
  }
`;

const SongRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const SongName = styled.h3`
  color: #ffffff;
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
  flex: 1;
`;

const SongDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const CurrentAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #cc0000;
`;

const TargetAmount = styled.div`
  font-size: 1rem;
  color: #888;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => props.status === 'active' ? '#cc0000' : '#666'};
  color: white;
`;

const PrintCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(204, 0, 0, 0.2);
  border-radius: 6px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: #cc0000;
    transform: translateY(-2px);
  }
`;

const PrintRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const PrintName = styled.h3`
  color: #ffffff;
  font-size: 1.2rem;
  margin: 0;
  font-weight: 600;
  flex: 1;
`;

const PrintDetails = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    flex-direction: column;
    align-items: flex-end;
  }
`;

const PrintInfo = styled.div`
  text-align: right;
`;

const PrintLabel = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 0.25rem;
`;

const PrintValue = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #ffffff;
`;

const PrintDifference = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #cc0000;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #444;
    border-top: 4px solid #cc0000;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #999;
`;

function AdvancedPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSongName, setNewSongName] = useState('');
  const [newSongTarget, setNewSongTarget] = useState(10);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [selectedSongIds, setSelectedSongIds] = useState([]);
  const [savingGroup, setSavingGroup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize active tab from query parameter if present
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && ['overview', 'statistics', 'print', 'folder', 'groups'].includes(tab)) {
      setActiveTab(tab);
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [songsData, statsData, groupsData] = await Promise.all([
        songAPI.getSongs('', 'name', 'ASC'),
        songAPI.getStats(),
        songAPI.getGroups()
      ]);
      setSongs(songsData);
      setStats(statsData);
      setGroups(groupsData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadGroupEditor = async (groupId) => {
    try {
      const group = await songAPI.getGroup(groupId);
      setSelectedGroupId(groupId);
      setEditingGroup(group);
      setGroupNameDraft(group.name);
      setSelectedSongIds(group.songs.map((s) => s.id));
    } catch (error) {
      console.error('Error loading group:', error);
      toast.error('Failed to load group');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    try {
      const group = await songAPI.createGroup(newGroupName.trim());
      toast.success('Group created');
      setNewGroupName('');
      await loadData();
      loadGroupEditor(group.id);
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error(error.response?.data?.error || 'Failed to create group');
    }
  };

  const handleToggleSongInGroup = (songId) => {
    setSelectedSongIds((prev) =>
      prev.includes(songId) ? prev.filter((id) => id !== songId) : [...prev, songId]
    );
  };

  const handleSaveGroup = async () => {
    if (!selectedGroupId) return;
    try {
      setSavingGroup(true);
      const updated = await songAPI.updateGroup(selectedGroupId, {
        name: groupNameDraft.trim(),
        songIds: selectedSongIds
      });
      setEditingGroup(updated);
      toast.success('Group saved');
      await loadData();
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error(error.response?.data?.error || 'Failed to save group');
    } finally {
      setSavingGroup(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroupId || !editingGroup) return;
    if (!window.confirm(`Delete group "${editingGroup.name}"?`)) return;
    try {
      await songAPI.deleteGroup(selectedGroupId);
      toast.success('Group deleted');
      setSelectedGroupId(null);
      setEditingGroup(null);
      setSelectedSongIds([]);
      await loadData();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const handleCopyShareLink = async () => {
    if (!editingGroup?.share_slug) return;
    const url = `${window.location.origin}/group/${editingGroup.share_slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch (error) {
      // Fallback for older browsers / insecure contexts
      window.prompt('Copy share link:', url);
    }
  };

  const handleAddSong = async (e) => {
    e.preventDefault();
    if (!newSongName.trim()) return;

    try {
      await songAPI.createSong({
        name: newSongName.trim(),
        targetAmount: parseInt(newSongTarget)
      });
      
      toast.success('Song added successfully!');
      setNewSongName('');
      setNewSongTarget(10);
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error('Error adding song:', error);
      toast.error(error.response?.data?.error || 'Failed to add song');
    }
  };

  const handleSongClick = (songId) => {
    navigate(`/advanced/song/${songId}`);
  };

  const handlePrintSongClick = (songId) => {
    navigate(`/print/${songId}`);
  };

  const printSongs = songs.filter(song => song.status === 'active' && song.current_amount < song.target_amount);
  const qrCodesNeeded = songs.filter(song => !song.qr_page);
  const folderSongs = songs.filter(song => song.need_count_confirm === true);

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TabsContainer>
        <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Overview
        </Tab>
        <Tab active={activeTab === 'statistics'} onClick={() => setActiveTab('statistics')}>
          Statistics
        </Tab>
        <Tab active={activeTab === 'print'} onClick={() => setActiveTab('print')}>
          Print
        </Tab>
        <Tab active={activeTab === 'folder'} onClick={() => setActiveTab('folder')}>
          Folder
        </Tab>
        <Tab active={activeTab === 'groups'} onClick={() => setActiveTab('groups')}>
          Groups
        </Tab>
      </TabsContainer>

      <TabContent>
        {activeTab === 'overview' && (
          <>
            <Section>
              {!showAddForm ? (
                <Button onClick={() => setShowAddForm(true)}>
                  + Add Song
                </Button>
              ) : (
                <AddForm onSubmit={handleAddSong}>
                  <FormGroup>
                    <Label htmlFor="name">Song Name</Label>
                    <Input
                      type="text"
                      id="name"
                      value={newSongName}
                      onChange={(e) => setNewSongName(e.target.value)}
                      placeholder="Enter song name"
                      required
                      autoFocus
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="target">Target Amount</Label>
                    <Input
                      type="number"
                      id="target"
                      value={newSongTarget}
                      onChange={(e) => setNewSongTarget(e.target.value)}
                      min="0"
                    />
                  </FormGroup>
                  <Button type="submit">Add</Button>
                  <Button type="button" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </AddForm>
              )}
            </Section>

            <Section>
              <SongGrid>
                {songs.length === 0 ? (
                  <EmptyState>
                    <h3>No songs found</h3>
                  </EmptyState>
                ) : (
                  songs.map((song) => (
                    <SongCard
                      key={song.id}
                      onClick={() => handleSongClick(song.id)}
                      style={{ opacity: song.status === 'archived' ? 0.5 : 1 }}
                    >
                      <SongRow>
                        <SongName style={{ color: song.status === 'archived' ? '#888' : '#ffffff' }}>
                          {song.name}
                          {!song.pdf_filename && (
                            <span style={{ color: '#cc0000', marginLeft: '0.5rem', fontSize: '0.9rem' }} title="No PDF uploaded">
                              ⚠️
                            </span>
                          )}
                        </SongName>
                        <SongDetails>
                          <CurrentAmount style={{ color: song.status === 'archived' ? '#666' : '#cc0000' }}>
                            {song.current_amount}
                          </CurrentAmount>
                          <TargetAmount style={{ color: song.status === 'archived' ? '#666' : '#888' }}>
                            / {song.target_amount}
                          </TargetAmount>
                        </SongDetails>
                      </SongRow>
                      <StatusBadge status={song.status}>
                        {song.status}
                      </StatusBadge>
                    </SongCard>
                  ))
                )}
              </SongGrid>
            </Section>
          </>
        )}

        {activeTab === 'statistics' && (
          <Section>
            <SectionTitle>Statistics</SectionTitle>
            {stats && (
              <StatsContainer>
                <StatCard>
                  <StatValue>{stats.totalSongs}</StatValue>
                  <StatLabel>Total Songs</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{stats.activeSongs}</StatValue>
                  <StatLabel>Active</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{stats.completedSongs}</StatValue>
                  <StatLabel>Completed</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{stats.totalPrinted}</StatValue>
                  <StatLabel>Total Printed</StatLabel>
                </StatCard>
              </StatsContainer>
            )}
          </Section>
        )}

        {activeTab === 'print' && (
          <Section>
            <SectionTitle>Songs to Print</SectionTitle>
            {(printSongs.length === 0 && qrCodesNeeded.length === 0) ? (
              <EmptyState>
                <h3>All songs are up to date!</h3>
                <p>No songs need printing.</p>
              </EmptyState>
            ) : (
              <SongGrid>
                {qrCodesNeeded.length > 0 && (
                  <PrintCard onClick={() => navigate('/print/qr-codes')}>
                    <PrintRow>
                      <PrintName>Print QR Codes</PrintName>
                      <PrintDetails>
                        <PrintInfo>
                          <PrintLabel>Songs</PrintLabel>
                          <PrintValue>{qrCodesNeeded.length}</PrintValue>
                        </PrintInfo>
                      </PrintDetails>
                    </PrintRow>
                  </PrintCard>
                )}
                {printSongs.map((song) => (
                  <PrintCard
                    key={song.id}
                    onClick={() => handlePrintSongClick(song.id)}
                  >
                    <PrintRow>
                      <PrintName>{song.name}</PrintName>
                      <PrintDetails>
                        <PrintInfo>
                          <PrintLabel>Current</PrintLabel>
                          <PrintValue>{song.current_amount}</PrintValue>
                        </PrintInfo>
                        <PrintInfo>
                          <PrintLabel>Target</PrintLabel>
                          <PrintValue>{song.target_amount}</PrintValue>
                        </PrintInfo>
                        <PrintInfo>
                          <PrintLabel>Need</PrintLabel>
                          <PrintDifference>
                            {song.target_amount - song.current_amount}
                          </PrintDifference>
                        </PrintInfo>
                      </PrintDetails>
                    </PrintRow>
                  </PrintCard>
                ))}
              </SongGrid>
            )}
          </Section>
        )}

        {activeTab === 'folder' && (
          <Section>
            <SectionTitle>Songs Needing Count Confirmation</SectionTitle>
            {folderSongs.length === 0 ? (
              <EmptyState>
                <h3>All songs confirmed!</h3>
                <p>No songs need count confirmation.</p>
              </EmptyState>
            ) : (
              <SongGrid>
                {folderSongs.map((song) => (
                  <PrintCard
                    key={song.id}
                    onClick={() => navigate(`/folder/song/${song.id}`)}
                  >
                    <PrintRow>
                      <PrintName>{song.name}</PrintName>
                      <PrintDetails>
                        <PrintInfo>
                          <PrintLabel>Current</PrintLabel>
                          <PrintValue>{song.current_amount}</PrintValue>
                        </PrintInfo>
                      </PrintDetails>
                    </PrintRow>
                  </PrintCard>
                ))}
              </SongGrid>
            )}
          </Section>
        )}

        {activeTab === 'groups' && (
          <>
            <Section>
              <SectionTitle>Create Group</SectionTitle>
              <AddForm onSubmit={handleCreateGroup}>
                <FormGroup>
                  <Label htmlFor="groupName">Group Name</Label>
                  <Input
                    type="text"
                    id="groupName"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g. Concert May"
                    required
                  />
                </FormGroup>
                <Button type="submit">Create</Button>
              </AddForm>
            </Section>

            <Section>
              <SectionTitle>Groups</SectionTitle>
              {groups.length === 0 ? (
                <EmptyState>
                  <h3>No groups yet</h3>
                  <p>Create a group, add songs, then copy the share link.</p>
                </EmptyState>
              ) : (
                <SongGrid>
                  {groups.map((group) => (
                    <SongCard
                      key={group.id}
                      onClick={() => loadGroupEditor(group.id)}
                      style={{
                        borderColor: selectedGroupId === group.id ? '#cc0000' : undefined
                      }}
                    >
                      <SongRow>
                        <SongName>{group.name}</SongName>
                        <CurrentAmount>{group.song_count}</CurrentAmount>
                      </SongRow>
                      <StatusBadge status="active">
                        {group.song_count} song{group.song_count === 1 ? '' : 's'}
                      </StatusBadge>
                    </SongCard>
                  ))}
                </SongGrid>
              )}
            </Section>

            {editingGroup && (
              <Section>
                <SectionTitle>Edit: {editingGroup.name}</SectionTitle>
                <FormGroup style={{ marginBottom: '1rem' }}>
                  <Label htmlFor="editGroupName">Name</Label>
                  <Input
                    type="text"
                    id="editGroupName"
                    value={groupNameDraft}
                    onChange={(e) => setGroupNameDraft(e.target.value)}
                  />
                </FormGroup>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                  <Button type="button" onClick={handleCopyShareLink}>
                    Copy Share Link
                  </Button>
                  <Button
                    type="button"
                    onClick={() => window.open(`/group/${editingGroup.share_slug}`, '_blank')}
                    style={{ background: '#0066cc' }}
                  >
                    Open Share Page
                  </Button>
                  <Button type="button" onClick={handleSaveGroup} disabled={savingGroup}>
                    {savingGroup ? 'Saving…' : 'Save Group'}
                  </Button>
                  <Button type="button" onClick={handleDeleteGroup} style={{ background: '#666' }}>
                    Delete
                  </Button>
                </div>

                <Label>Songs in group</Label>
                <div style={{
                  maxHeight: '420px',
                  overflowY: 'auto',
                  marginTop: '0.75rem',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  padding: '0.75rem'
                }}>
                  {songs.map((song) => (
                    <label
                      key={song.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.45rem 0.25rem',
                        color: '#ddd',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.05)'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedSongIds.includes(song.id)}
                        onChange={() => handleToggleSongInGroup(song.id)}
                      />
                      <span style={{ flex: 1 }}>{song.name}</span>
                      {!song.pdf_filename && (
                        <span style={{ color: '#cc0000', fontSize: '0.85rem' }}>no PDF</span>
                      )}
                    </label>
                  ))}
                </div>
                <p style={{ color: '#888', marginTop: '0.75rem', fontSize: '0.9rem' }}>
                  {selectedSongIds.length} selected · share URL: /group/{editingGroup.share_slug}
                </p>
              </Section>
            )}
          </>
        )}
      </TabContent>
    </PageContainer>
  );
}

export default AdvancedPage;
